import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './Dialog';
import { Button } from './Button';

/**
 * EmptySourceField — E-41 field-scale empty-source treatment.
 *
 * Field-scale application of the E-41 ruling
 * (Iteration_21/UAT_0821_DECISION_REGISTER :113/:235):
 *   DISABLED treatment + modal (title + context), primary CTA to the
 *   relevant Settings page.
 *
 * The trigger LOOKS disabled but is not `disabled` — clicking it is how
 * the user reaches the CTA that fixes the empty state.
 *
 * ── Consumers (PR-72 Commit 4) ─────────────────────────────────────────
 *
 * D-17: `WorkOrderDetail.js` — customer-type row when `customer_types`
 * settings list is empty. Sub-area consumers (E-41's original scope) will
 * pass their own title/body/CTA copy.
 *
 * ── PORTICA-83: WHY THE CTA IS A HARD NAVIGATION ──────────────────────
 *
 * The CTA uses a raw <a href=…>, NOT react-router's <Link>. This is
 * DELIBERATE. `SettingsProvider`'s enum cache (useSettings.js) fills
 * once at provider mount from Object.values(SETTINGS_LISTS) (:129, :300)
 * and has NO invalidation path — `refresh(listType)` exists at :296 but
 * has zero callers. SettingsProvider wraps the entire router
 * (App.js:118-259), so client-side navigation never remounts it.
 *
 * Consequence if we used <Link>: user opens the modal → navigates to
 * Settings → adds a customer type (write succeeds, settingsListCache
 * invalidates) → navigates back → SettingsProvider has NOT remounted,
 * so getEnumLabel still reads an empty list → the field STILL shows
 * EmptySourceField. The E-41 flow completes into a dead end.
 *
 * A hard <a href=…> forces a full document load, which remounts the
 * whole app including SettingsProvider, which refetches enum lists.
 *
 * ── Remove this workaround when PORTICA-83 lands ──────────────────────
 *
 * PORTICA-83 proposes a version counter in settingsListCache bumped by
 * the existing invalidateSettingsListCache call, observed by
 * SettingsProvider. Once that reactive layer exists:
 *   1) Replace the raw <a href=…> below with <Link to={ctaHref}>.
 *   2) Update EmptySourceField.test.js hard-nav guard (it will redden
 *      by design at that point — flip the assertion).
 *   3) Delete this comment block through the ── PORTICA-83 line.
 *
 * The hard-nav guard test is the only in-code enforcement of this
 * workaround. Do not remove it before PORTICA-83.
 *
 * ── Copy (dispatch 415 §4 + 421 §3) ───────────────────────────────────
 *
 * `modalTitle` and `modalBody` are consumer-supplied. No default copy —
 * shipping a stakeholder-unruled default in a shared primitive would
 * make it the de-facto standard by accident (§8.11).
 *
 * IMPORTANT — the no-default protection is not enough on its own. The
 * moment a second consumer arrives, the natural move is to copy the
 * first consumer's wording as a starting point. Provisional text then
 * becomes the house standard by imitation rather than by decision, and
 * no marker anywhere says it happened. If you are here to add a second
 * consumer:
 *
 *   1. Check whether the first consumer's copy is still provisional
 *      (grep for "COPY: PROVISIONAL" — as of PR-72 Commit 4,
 *      CustomerTypeCell carries such a marker, ticket PORTICA-86).
 *   2. If yes, write your own provisional copy independently and flag
 *      it the same way with your own owner and ticket. Do not copy the
 *      other consumer's strings.
 *   3. If no (i.e. the first consumer's copy has been ruled), you may
 *      adopt or diverge as the ruling permits.
 *
 * Two flagged provisionals are honest; one unflagged copy of a
 * provisional is how a draft becomes a standard.
 */
export default function EmptySourceField({
  fieldLabel,
  triggerText,
  modalTitle,
  modalBody,
  ctaLabel,
  ctaHref,
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      {fieldLabel && (
        <div className="text-xs font-medium text-gray-500 uppercase">
          {fieldLabel}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-0.5 inline-flex items-center gap-1 rounded-md border border-dashed border-[color:var(--color-border)] bg-gray-50 px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-action-primary)]"
      >
        {triggerText}
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} size="md">
        <DialogHeader onClose={() => setOpen(false)}>{modalTitle}</DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">{modalBody}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          {/*
            PORTICA-83 hard-navigation workaround — see docblock above.
            Do NOT swap this <a> for <Link>. The hard-nav guard test in
            EmptySourceField.test.js catches that specific edit.
          */}
          <a
            href={ctaHref}
            className="inline-flex items-center rounded-md bg-[color:var(--color-action-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-action-primary)]"
          >
            {ctaLabel}
          </a>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
